import {
  Lit3formBlock,
  html,
  unsafeHTML,
  classMap,
} from '@Root/lib/lit-3form-block';
import { styles } from './css';
import {
  fetchCart,
  checkProductAvailability,
  addToCart,
} from '@Root/spree/api';
import {
  updateCartIndicator,
  changeProductNameInModal,
  changeProductImageInModal,
  changeProductSizeInfoInModal,
} from '@Root/spree/dom';
import find from 'lodash.find';
import { logErrorToSentry } from '../../../helpers/helpers';
import {
  analyticsCtaDataObject,
  handleAdobeNavEvent,
} from '../../../helpers/adobe-analytics';

export class Hank extends Lit3formBlock {
  static get characterName() {
    return 'hank';
  }

  static get characterProperties() {
    return {
      header: { type: String },
      description: { type: String },
      cta: { type: String },
      _ctaDisabled: { type: Boolean },
      _ctaHidden: { type: Boolean },
      _productProperties: { type: Object },
      _productIsAvailable: { type: Boolean },
    };
  }

  static get characterStyles() {
    return [...styles];
  }

  constructor() {
    super();

    this._ctaDisabled = false;
    this._ctaHidden = false;
    this._productProperties = {};
    this._productIsAvailable = false;
  }

  async connectedCallback() {
    super.connectedCallback();

    try {
      await this.setupSpreeData();
    } catch (error) {
      this._ctaHidden = !this.ctaUrl;
      logErrorToSentry(error);
    }
  }

  render() {
    return html`
      <div class="block-wrapper">
        <div
          class="${classMap({
            'block-grid': true,
            'hank-layout': true,
            'mobile-rows-1': this.oneRowNeeded,
            'mobile-rows-2': this.twoRowsNeeded,
            'mobile-rows-3': this.threeRowsNeeded,
            'mobile-rows-4': this.fourRowsNeeded,
          })}"
        >
          ${!!this.headerText
            ? html`
                <h2 class="block-title">${unsafeHTML(this.headerText)}</h2>
              `
            : html``}
          ${!!this.descriptionText
            ? html`
                <p class="block-description">
                  ${unsafeHTML(this.descriptionText)}
                </p>
              `
            : html``}
          ${this.hasCtaData
            ? html`
                <cta-button
                  class="cta-btn"
                  cta-data=${this.ctaData}
                  cta-type="secondary"
                  @click="${this.ctaButtonClicked}"
                  .ctaAnalyticsData=${analyticsCtaDataObject(
                    this.ctaData,
                    this.headerText
                  )}
                  .hidden="${this._ctaHidden}"
                  .disabled="${this._ctaDisabled}"
                ></cta-button>
              `
            : html``}
          ${this.hasTextLinkData
            ? html`
                <cta-text
                  class="cta-text"
                  cta-data=${this.textLinkCtaData}
                  .ctaAnalyticsData=${analyticsCtaDataObject(
                    this.textLinkCtaData,
                    this.headerText
                  )}
                ></cta-text>
              `
            : html``}
          ${this.imagesArr.length
            ? html`
                <div class="images">
                  ${this.imagesArr.map(i => this.imageHTML(i[1]))}
                </div>
              `
            : html``}
          ${this.horizontalRule}
        </div>
      </div>
    `;
  }

  get textLinkCtaData() {
    return JSON.stringify({
      text: this.content.text_link_text,
      url: this.content.text_link_url,
    });
  }

  imageHTML(image) {
    return html`
      <div class="image-container">
        ${image.url
          ? html`
              <a
                href="${image.url}"
                @click="${event => {
                  event.preventDefault();
                  handleAdobeNavEvent({
                    analyticsCtaDataObject: this.analyticsDataObject(image),
                    event,
                  });
                }}"
              >
                <img
                  class="image"
                  src=${this.imagebossUrl(image.src, null, '365')}
                />
              </a>
              <cta-text
                cta-data=${this.imageCtaInfo(image)}
                .ctaAnalyticsData=${this.analyticsDataObject(image)}
              ></cta-text>
            `
          : html`
              <img
                class="image"
                src=${this.imagebossUrl(image.src, null, '365')}
              />
            `}
      </div>
    `;
  }

  analyticsDataObject(image) {
    image.destinationUrl = image.url;
    const ctaObj = analyticsCtaDataObject(image, this.headerText);
    return ctaObj;
  }

  get oneRowNeeded() {
    return this.mobileRowsNeeded === 1;
  }

  get twoRowsNeeded() {
    return this.mobileRowsNeeded === 2;
  }

  get threeRowsNeeded() {
    return this.mobileRowsNeeded === 3;
  }

  get fourRowsNeeded() {
    return this.mobileRowsNeeded === 4;
  }

  get mobileRowsNeeded() {
    let count = 0;
    if (!!this.headerText || !!this.hasCtaData) count++;
    if (!!this.hasTextLinkData) count++;
    if (!!this.descriptionText) count++;
    if (!!this.imagesArr.length) count++;
    return count;
  }

  get headerText() {
    return this.withLineBreaks(this.content.header) || '';
  }

  get descriptionText() {
    return this.withLineBreaks(this.content.description) || '';
  }

  get imagesArr() {
    return this.content.images;
  }

  get ctaData() {
    return JSON.stringify(this.content.cta);
  }

  get hasCtaData() {
    return !!this.content.cta && !!this.content.cta.text;
  }

  get hasTextLinkData() {
    return !!this.content.text_link_url && !!this.content.text_link_text;
  }

  get productId() {
    return this.content.spree_product?.id;
  }

  get sampleTypeSlug() {
    return this.content.spree_product?.sample_type_slug;
  }

  get sampleTypeText() {
    return this.content.spree_product?.sample_type_text;
  }

  get spreeProductTitle() {
    return this.content.spree_product?.modal_title;
  }

  get spreeProductSubtitle() {
    return this.content.spree_product?.modal_subtitle;
  }

  get spreeProductImage() {
    return this.content.spree_product?.image_src;
  }

  get ctaUrl() {
    return this.content?.cta?.url;
  }

  async setupSpreeData() {
    if (!this.productId || !!this.ctaUrl) return;

    this.cart = await fetchCart();
    if (this.cart) updateCartIndicator(this.cart.data.attributes.item_count);

    this.includedPropertiesById = {};
    const productAvailabilityResponse = await checkProductAvailability(
      this.productId
    );

    this._productIsAvailable = !productAvailabilityResponse.isFail();
    if (this._productIsAvailable) {
      this._productProperties = productAvailabilityResponse.success().included;
    } else {
      this._ctaDisabled = true;
    }
  }

  async ctaButtonClicked() {
    if (!this._productIsAvailable || this._ctaDisabled) return;

    this.cart = await addToCart(
      this.productId,
      this.addToCartQuantity,
      this.sampleTypeSlug
    );

    if (this.cart) {
      updateCartIndicator(this.cart.item_count);

      const productSize = find(this._productProperties, {
        attributes: { name: 'size' },
      });

      changeProductSizeInfoInModal(
        productSize.attributes.value,
        this.sampleTypeText
      );
      changeProductNameInModal(
        this.spreeProductTitle,
        this.spreeProductSubtitle
      );
      changeProductImageInModal(this.spreeProductImage);
    }
  }

  imageCtaInfo(image) {
    return JSON.stringify({
      url: image.url,
      text: image.text,
    });
  }
}
Hank.defineElement();
